/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/credential-health/pages/CredentialHealthDashboard.tsx
 * layer: web
 * area: credential-health
 * kind: page
 * web_root: pages
 * relpath: web/src/capabilities/credential-health/pages/CredentialHealthDashboard.tsx
 * filename: CredentialHealthDashboard.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  KeyIcon,
  WrenchScrewdriverIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { api, getErrorMessage } from '@/platform/api-client/client';
import { confirmAction } from '@/platform/design-system/components/shared/confirmAction';
import { clsx } from 'clsx';
import { formatRelativeTime } from '@/platform/shared/lib/utils';
import { Link } from 'react-router-dom';
import { PageTabs } from '@/platform/design-system/components/shared/PageTabs';
import { useWorkflowGroup } from '@/workflows/automation/hooks/useWorkflowGroup';
import { DegradedState } from '@/platform/design-system/components/ui/DegradedState';
import { SkeletonStatCard, SkeletonTableRow } from '@/platform/design-system/components/ui/SkeletonPatterns';

// ---------- Types ----------

interface DashboardData {
  healthScore: number | null;
  totalCredentials: number;
  brokenCount: number;
  duplicateCount: number;
  needsRotationCount: number;
  lastScanAt: string | null;
  recentScans: Scan[];
}

interface Scan {
  id: string;
  status: string;
  triggeredBy: string;
  testTypes: string[];
  totalTests: number;
  passed: number;
  failed: number;
  errors: number;
  skipped: number;
  healthScore: number | null;
  duplicateGroups: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

interface CredentialHealth {
  id: string;
  credentialId: string;
  lastTestStatus: string;
  lastTestAt: string | null;
  lastSuccessAt: string | null;
  consecutiveFailures: number;
  isBroken: boolean;
  isDuplicate: boolean;
  healthScore: number | null;
  passwordAgeDays: number | null;
  needsRotation: boolean;
}

interface DuplicateGroup {
  id: string;
  matchType: string;
  credentialIds: string[];
  description: string;
  isResolved: boolean;
  createdAt: string;
}

interface ServiceAuditResult {
  id: string;
  deviceId: string;
  deviceName: string;
  serviceName: string;
  displayName: string;
  serviceAccount: string;
  isAdminAccount: boolean;
  isGenericAccount: boolean;
  startupType: string;
  serviceStatus: string;
}

// ---------- Component ----------

export function CredentialHealthDashboard({ embedded }: { embedded?: boolean } = {}) {
  const { palette } = useWorkflowGroup();
  const queryClient = useQueryClient();
  const [scanInProgress, setScanInProgress] = useState(false);

  const {
    data: dashboard,
    isError: dashboardIsError,
    isFetching: dashboardIsFetching,
    isLoading,
    refetch: refetchDashboard,
  } = useQuery<DashboardData>({
    queryKey: ['credential-health', 'dashboard'],
    queryFn: async () => {
      const res = await api.get<DashboardData>('/credential-health/dashboard');
      return res.data;
    },
  });

  const brokenQuery = useQuery<CredentialHealth[] | null>({
    queryKey: ['credential-health', 'broken'],
    queryFn: async () => {
      const res = await api.get<CredentialHealth[] | null>('/credential-health/credentials/broken');
      return res.data;
    },
    enabled: !!dashboard,
  });

  const duplicatesQuery = useQuery<DuplicateGroup[] | null>({
    queryKey: ['credential-health', 'duplicates'],
    queryFn: async () => {
      const res = await api.get<DuplicateGroup[] | null>('/credential-health/duplicates');
      return res.data;
    },
    enabled: !!dashboard,
  });

  const serviceAuditQuery = useQuery<ServiceAuditResult[] | null>({
    queryKey: ['credential-health', 'service-accounts'],
    queryFn: async () => {
      const res = await api.get<ServiceAuditResult[] | null>('/credential-health/service-accounts');
      return res.data;
    },
    enabled: !!dashboard,
  });

  // Go nil slices arrive as null: that is an empty answer, not a failure.
  const brokenCreds = brokenQuery.data === null ? [] : brokenQuery.data;
  const duplicates = duplicatesQuery.data === null ? [] : duplicatesQuery.data;
  const serviceAudit = serviceAuditQuery.data === null ? [] : serviceAuditQuery.data;

  const runScanMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/credential-health/scans', {
        testTypes: [
          'web_portal',
          'rdp',
          'ssh',
          'sql_server',
          'windows_login',
          'service_account_audit',
          'duplicate_detection',
        ],
      });
      return res.data;
    },
    onMutate: () => setScanInProgress(true),
    onSettled: () => {
      setScanInProgress(false);
      queryClient.invalidateQueries({ queryKey: ['credential-health'] });
    },
  });

  const resolveDuplicateMutation = useMutation({
    meta: { errorFeedback: 'inline' },
    mutationFn: async (groupId: string) => {
      await api.post(`/credential-health/duplicates/${groupId}/resolve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-health'] });
    },
  });

  if (isLoading) {
    return (
      <div className="page-enter space-y-6 p-6">
        {!embedded && (
          <PageTabs
            tabs={[
              { label: 'Vault', href: '/switchboard/vault' },
              { label: 'Shared', href: '/switchboard/vault?tab=shared' },
              { label: 'Audit', href: '/switchboard/vault?tab=audit' },
              { label: 'Settings', href: '/switchboard/configure/vault' },
              { label: 'Health', href: '/switchboard/vault?tab=health&hsub=dashboard' },
            ]}
            className="mb-4"
            accentColor={palette.accent}
          />
        )}
        <SkeletonStatCard count={4} />
        <SkeletonTableRow count={5} />
      </div>
    );
  }

  if (dashboardIsError || !dashboard) {
    return (
      <div className="page-enter space-y-6 p-6">
        {!embedded && (
          <PageTabs
            tabs={[
              { label: 'Vault', href: '/switchboard/vault' },
              { label: 'Shared', href: '/switchboard/vault?tab=shared' },
              { label: 'Audit', href: '/switchboard/vault?tab=audit' },
              { label: 'Settings', href: '/switchboard/configure/vault' },
              { label: 'Health', href: '/switchboard/vault?tab=health&hsub=dashboard' },
            ]}
            className="mb-4"
            accentColor={palette.accent}
          />
        )}
        <DegradedState
          title="Credential Health could not be loaded"
          description="The canonical credential-health service did not return a usable dashboard. No score, broken-credential count, duplicate count, or remediation readiness has been inferred."
          recoveryHint="Credential secrets remain in Cerberus and are not exposed by this failure state."
          onRetry={() => void refetchDashboard()}
          isRetrying={dashboardIsFetching}
        />
      </div>
    );
  }

  // null = no scan has produced a score yet; never shown as 0 / Critical.
  const healthScore = dashboard?.healthScore ?? null;
  const scored = healthScore !== null;
  const scoreColor = !scored
    ? 'text-[rgb(var(--color-text-tertiary))]'
    : healthScore >= 80
      ? 'text-emerald-500'
      : healthScore >= 50
        ? 'text-amber-500'
        : 'text-red-500';
  const scoreBg = !scored
    ? ''
    : healthScore >= 80
      ? 'bg-emerald-500/10'
      : healthScore >= 50
        ? 'bg-amber-500/10'
        : 'bg-red-500/10';

  const confirmResolveDuplicate = async (group: DuplicateGroup) => {
    const confirmed = await confirmAction({
      title: `Mark "${group.description}" as resolved?`,
      message: `${group.credentialIds.length} credentials matched by ${group.matchType.replace(/_/g, ' ')}. The group is hidden from this dashboard; no credential is changed or merged.`,
      variant: 'warning',
      confirmLabel: 'Mark resolved',
    });
    if (confirmed) resolveDuplicateMutation.mutate(group.id);
  };

  return (
    <div className="page-enter space-y-6">
      {/* Header */}
      <div className="control-surface flex items-center justify-between rounded-[1.75rem] px-5 py-5 md:px-6">
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--color-text-primary))]">
            Credential Health
          </h1>
          <p className="mt-1 text-sm text-[rgb(var(--color-text-secondary))]">
            Monitor, test, and rotate credentials across your vault
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/switchboard/vault?tab=health&hsub=remediation"
            className="control-action rounded-xl px-4 py-2 text-sm font-medium"
          >
            <WrenchScrewdriverIcon className="w-4 h-4 inline mr-2" />
            Remediation Queue
          </Link>
          <button
            onClick={() => runScanMutation.mutate()}
            disabled={scanInProgress}
            className="workbench-chip-active rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
          >
            {scanInProgress ? (
              <ArrowPathIcon className="w-4 h-4 inline mr-2 motion-loading-spin" />
            ) : (
              <PlayIcon className="w-4 h-4 inline mr-2" />
            )}
            Run Full Scan
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Health Score */}
        <div className={clsx('control-surface rounded-xl p-5', scoreBg)}>
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                'w-16 h-16 rounded-full border-4 flex items-center justify-center text-xl font-bold',
                scoreColor,
                !scored
                  ? 'border-[rgb(var(--color-border-primary))]'
                  : healthScore >= 80
                    ? 'border-emerald-500/30'
                    : healthScore >= 50
                      ? 'border-amber-500/30'
                      : 'border-red-500/30'
              )}
            >
              {scored ? Math.round(healthScore) : '—'}
            </div>
            <div>
              <p className="text-sm text-[rgb(var(--color-text-tertiary))]">Health Score</p>
              <p className={clsx('text-lg font-semibold', scoreColor)}>
                {!scored
                  ? 'Not scanned yet'
                  : healthScore >= 80
                    ? 'Healthy'
                    : healthScore >= 50
                      ? 'At Risk'
                      : 'Critical'}
              </p>
            </div>
          </div>
        </div>

        <KPICard
          icon={KeyIcon}
          label="Total Credentials"
          value={dashboard?.totalCredentials ?? 0}
          color="text-[rgb(var(--color-text-primary))]"
        />
        <KPICard
          icon={XCircleIcon}
          label="Broken"
          value={dashboard?.brokenCount ?? 0}
          color="text-red-400"
          alert={dashboard?.brokenCount ? dashboard.brokenCount > 0 : false}
        />
        <KPICard
          icon={DocumentDuplicateIcon}
          label="Duplicates"
          value={dashboard?.duplicateCount ?? 0}
          color="text-amber-400"
          alert={dashboard?.duplicateCount ? dashboard.duplicateCount > 0 : false}
        />
        <KPICard
          icon={ArrowPathIcon}
          label="Needs Rotation"
          value={dashboard?.needsRotationCount ?? 0}
          color="text-orange-400"
          alert={dashboard?.needsRotationCount ? dashboard.needsRotationCount > 0 : false}
        />
      </div>

      {resolveDuplicateMutation.isError && (
        <p className="text-sm text-red-500" role="alert">
          The duplicate group could not be marked resolved:{' '}
          {getErrorMessage(resolveDuplicateMutation.error, 'the server did not confirm it')}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Broken Credentials */}
        <div className="control-surface overflow-hidden rounded-xl">
          <div className="flex items-center justify-between border-b border-[rgb(var(--color-border-primary))] px-5 py-4">
            <h2 className="text-sm font-semibold text-[rgb(var(--color-text-primary))]">
              Broken Credentials
            </h2>
            <span className="text-xs text-[rgb(var(--color-text-tertiary))]">
              {brokenCreds ? `${brokenCreds.length} found` : ''}
            </span>
          </div>
          <div className="max-h-80 divide-y divide-[rgb(var(--color-border-primary)/0.5)] overflow-y-auto">
            {brokenQuery.isError ? (
              <PanelFailure
                what="Broken credentials"
                error={brokenQuery.error}
                onRetry={() => void brokenQuery.refetch()}
              />
            ) : !brokenCreds ? (
              <PanelLoading />
            ) : brokenCreds.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-[rgb(var(--color-text-tertiary))]">
                <CheckCircleIcon className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                No broken credentials
              </div>
            ) : (
              brokenCreds.map((cred) => (
                <div
                  key={cred.id}
                  className="control-list-row px-5 py-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-[rgb(var(--color-text-primary))]">
                      {cred.credentialId.slice(0, 8)}...
                    </p>
                    <p className="text-xs text-[rgb(var(--color-text-tertiary))]">
                      {cred.consecutiveFailures} consecutive failures
                      {cred.lastTestAt && ` · Last tested ${formatRelativeTime(cred.lastTestAt)}`}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/10 text-red-400">
                    {cred.lastTestStatus}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Duplicate Groups */}
        <div className="control-surface overflow-hidden rounded-xl">
          <div className="flex items-center justify-between border-b border-[rgb(var(--color-border-primary))] px-5 py-4">
            <h2 className="text-sm font-semibold text-[rgb(var(--color-text-primary))]">
              Duplicate Groups
            </h2>
            <span className="text-xs text-[rgb(var(--color-text-tertiary))]">
              {duplicates ? `${duplicates.length} groups` : ''}
            </span>
          </div>
          <div className="max-h-80 divide-y divide-[rgb(var(--color-border-primary)/0.5)] overflow-y-auto">
            {duplicatesQuery.isError ? (
              <PanelFailure
                what="Duplicate groups"
                error={duplicatesQuery.error}
                onRetry={() => void duplicatesQuery.refetch()}
              />
            ) : !duplicates ? (
              <PanelLoading />
            ) : duplicates.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-[rgb(var(--color-text-tertiary))]">
                <CheckCircleIcon className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                No duplicates detected
              </div>
            ) : (
              duplicates.map((group) => (
                <div
                  key={group.id}
                  className="control-list-row px-5 py-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm text-[rgb(var(--color-text-primary))]">
                      {group.description}
                    </p>
                    <p className="text-xs text-[rgb(var(--color-text-tertiary))]">
                      {group.credentialIds.length} credentials · {group.matchType.replace('_', ' ')}
                    </p>
                  </div>
                  <button
                    onClick={() => void confirmResolveDuplicate(group)}
                    disabled={resolveDuplicateMutation.isPending}
                    className="control-action rounded-lg px-3 py-1 text-xs font-medium text-amber-500 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
                  >
                    Resolve
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Scans */}
        <div className="control-surface overflow-hidden rounded-xl">
          <div className="border-b border-[rgb(var(--color-border-primary))] px-5 py-4">
            <h2 className="text-sm font-semibold text-[rgb(var(--color-text-primary))]">
              Recent Scans
            </h2>
          </div>
          <div className="max-h-80 divide-y divide-[rgb(var(--color-border-primary)/0.5)] overflow-y-auto">
            {!dashboard?.recentScans || dashboard.recentScans.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-[rgb(var(--color-text-tertiary))]">
                <ClockIcon className="mx-auto mb-2 h-8 w-8 text-[rgb(var(--color-text-tertiary))]" />
                No scans yet
              </div>
            ) : (
              dashboard.recentScans.map((scan) => (
                <Link
                  key={scan.id}
                  to={`/portfolio/vault/health/scans/${scan.id}`}
                  className="control-list-row block px-5 py-3"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <ScanStatusBadge status={scan.status} />
                      <span className="text-sm text-[rgb(var(--color-text-secondary))]">
                        {scan.totalTests} tests
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[rgb(var(--color-text-tertiary))]">
                      {formatRelativeTime(scan.createdAt)} · {scan.triggeredBy}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-emerald-400">{scan.passed} passed</span>
                      {scan.failed > 0 && (
                        <span className="text-red-400">{scan.failed} failed</span>
                      )}
                      {scan.errors > 0 && (
                        <span className="text-amber-400">{scan.errors} errors</span>
                      )}
                    </div>
                    {scan.healthScore != null && (
                      <p className="mt-0.5 text-xs text-[rgb(var(--color-text-tertiary))]">
                        {Math.round(scan.healthScore)}% health
                      </p>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Service Account Findings */}
        <div className="control-surface overflow-hidden rounded-xl">
          <div className="flex items-center justify-between border-b border-[rgb(var(--color-border-primary))] px-5 py-4">
            <h2 className="text-sm font-semibold text-[rgb(var(--color-text-primary))]">
              Service Account Findings
            </h2>
            <span className="text-xs text-[rgb(var(--color-text-tertiary))]">
              {serviceAudit ? `${serviceAudit.length} services` : ''}
            </span>
          </div>
          <div className="max-h-80 divide-y divide-[rgb(var(--color-border-primary)/0.5)] overflow-y-auto">
            {serviceAuditQuery.isError ? (
              <PanelFailure
                what="Service account findings"
                error={serviceAuditQuery.error}
                onRetry={() => void serviceAuditQuery.refetch()}
              />
            ) : !serviceAudit ? (
              <PanelLoading />
            ) : serviceAudit.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-[rgb(var(--color-text-tertiary))]">
                <ServerIcon className="mx-auto mb-2 h-8 w-8 text-[rgb(var(--color-text-tertiary))]" />
                No service account issues
              </div>
            ) : (
              serviceAudit.map((svc) => (
                <div
                  key={svc.id}
                  className="control-list-row px-5 py-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm text-[rgb(var(--color-text-primary))]">
                      {svc.displayName || svc.serviceName}
                    </p>
                    <p className="text-xs text-[rgb(var(--color-text-tertiary))]">
                      {svc.deviceName} · Running as {svc.serviceAccount}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {svc.isAdminAccount && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/10 text-red-400">
                        Admin
                      </span>
                    )}
                    {svc.isGenericAccount && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/10 text-amber-400">
                        Generic
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function KPICard({
  icon: Icon,
  label,
  value,
  color,
  alert,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
  alert?: boolean;
}) {
  return (
    <div
      className={clsx('control-surface rounded-xl p-5', alert && 'border-red-500/24 bg-red-500/6')}
    >
      <div className="flex items-center gap-3">
        <Icon className={clsx('w-8 h-8', color)} />
        <div>
          <p className="text-sm text-[rgb(var(--color-text-tertiary))]">{label}</p>
          <p className={clsx('text-2xl font-bold', color)}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function ScanStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-tertiary))]',
    running: 'bg-accent/10 text-blue-400',
    completed: 'bg-emerald-500/10 text-emerald-400',
    failed: 'bg-red-500/10 text-red-400',
    cancelled: 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-tertiary))]',
  };

  return (
    <span
      className={clsx(
        'px-2 py-0.5 text-xs font-medium rounded-full',
        styles[status] || styles.pending
      )}
    >
      {status}
    </span>
  );
}

function PanelLoading() {
  return (
    <div className="px-5 py-8 text-center text-sm text-[rgb(var(--color-text-tertiary))]">
      Loading...
    </div>
  );
}

/** A panel whose source failed: never presented as a green "No ..." result. */
function PanelFailure({
  what,
  error,
  onRetry,
}: {
  what: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="px-5 py-8 text-center text-sm" role="alert">
      <p className="font-medium text-red-500">{what} could not be loaded</p>
      <p className="mt-1 text-xs text-[rgb(var(--color-text-tertiary))]">
        {getErrorMessage(error, 'The credential-health service did not answer.')} Nothing is
        assumed healthy.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="control-action mt-3 rounded-lg px-3 py-1 text-xs font-medium"
      >
        Retry
      </button>
    </div>
  );
}
